import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLanguages } from "@insite/client-framework/Store/Context/ContextSelectors";
import setLanguage from "@insite/client-framework/Store/Context/Handlers/SetLanguage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Globe from "@insite/mobius/Icons/Globe";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useEffect, useState} from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    showIcon = "showIcon",
}

const mapStateToProps = (state: ApplicationState) => ({
    languages: getLanguages(state),
    currentLanguage: state.context.session.language,
    location: getLocation(state),
});


const mapDispatchToProps = {
    setLanguage,
};

export interface LanguageMenuStyles {
    languageWrapper?: InjectableCss;
    languageIcon?: IconPresentationProps;
    languageSelect?: SelectPresentationProps;
}

export const languageMenuStyles: LanguageMenuStyles = {
    languageWrapper: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    languageIcon: {
        size: 22,
        src: Globe,
        css: css`
            margin-top: 10px;
        `,
    },
    languageSelect: {
        backgroundColor: "transparent",
        cssOverrides: {
            formField: css`
                & > div {
                    margin-top: 0px !important;
                }
            `,
            formInputWrapper: css`
                width: 83px;
            `,
            inputSelect: css`
                border: none;
                text-transform: capitalize;
                font-size: 12px;
                padding: 9px !important;
                &:focus {
                    outline: none;
                    // border: none;
                    // padding: 0 10px 0 10px;
                }
            `,
        },
        iconProps: {
            css: css`
                position: absolute;
                box-sizing: border-box;
                top: 2px;
                right: 0px !important;
                width: 33px;
                height: 33px;
                padding: 8px 10px !important;
                border-radius: 100%;
                pointer-events: none;
            `,
        },
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showIcon]: boolean;
    };
    urlData: any[];
    data: any[];
    path: string | undefined;
}

export const LogoImage = styled.img`
    height: 22px;
`;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;
const styles = languageMenuStyles;
export const LanguageMenu: FC<Props> = ({ 
        languages,
        currentLanguage,
        fields,
        setLanguage,
        urlData,
        data,
        location,
        path
    }) => {

    const[urlLangCode, setUrlLangCode] = useState(currentLanguage?.id);

    if (!languages || languages.length <= 1) {
        return null;
    }
    const styles = useMergeStyles("languageMenu", languageMenuStyles);

    const menuId = "languageMenu";

    useEffect(()=>{
        const localStoargeValue = window.localStorage.getItem("ptLanguage");
        path = window.location.search;
        const search = new URLSearchParams(path);
        const langValue = search.get("__ptLanguage");
        data = languages?.filter(v => v.languageCode === langValue);
        urlData = languages?.filter(v => v.languageCode === localStoargeValue);
        const urlLanguageId = urlData[0]?.id;
        const dataLanguageId = data[0]?.id;
        if(path) {
           if(getCookie("SetContextLanguageCode") !== localStoargeValue){
                setUrlLangCode(urlLanguageId);
           }
           if(langValue !== localStoargeValue || getCookie("SetContextLanguageCode") === localStoargeValue){
                setUrlLangCode(dataLanguageId);
           }
        } 
        if(!path) {
            if(getCookie("SetContextLanguageCode") !== localStoargeValue){
                setUrlLangCode(urlLanguageId);
            } 
            else {
                setUrlLangCode(dataLanguageId);
            }
        }
    },[])

    return (
        <StyledWrapper {...styles.languageWrapper}>
            {fields.showIcon &&
                (currentLanguage?.imageFilePath ? (
                    <LogoImage src={currentLanguage.imageFilePath} alt="" />
                ) : (
                    <Icon {...styles.languageIcon} />
                ))}
            <VisuallyHidden as="label" htmlFor={menuId} id={`${menuId}-label`}>
                {translate("Language")}
            </VisuallyHidden>
            <Select
                {...styles.languageSelect}
                uid={menuId}
                data-test-selector="languageSelector"
                onChange={event => setLanguage({ languageId: event.currentTarget.value })}
                value={urlLangCode}
		        hasLabel
            >
                {languages.map(c => (
                    <option value={c.id} key={c.id}>
                        {c.description}
                    </option>
                ))}
            </Select>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(LanguageMenu),
    definition: {
        displayName: "Language Menu",
        icon: "language",
        fieldDefinitions: [
            {
                name: fields.showIcon,
                displayName: "Show Icon",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
